import React, { useState, useRef, useEffect } from 'react';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { toast } from 'sonner';
import { Send, Zap, ArrowLeft, TrendingUp, AlertTriangle, BarChart3, Users, Search, Download, Check, Loader2 } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { SALES_REPORTS_DATA, REPORTS_CHAT_RESPONSES } from '../../data/salesReportsData';
import V4AdHocChat from './V4AdHocChat';
import {
  DAVID_REPORTS_OVERALL_DD,
  DAVID_REPORTS_REP_DD,
  DAVID_REPORTS_WINLOSS_DD,
  DAVID_REPORTS_MONTHLY_DD,
  DAVID_REPORTS_INSIGHT_DD,
  DAVID_REPORTS_KEYPRESS,
} from '../../data/davidReportsDDData';

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  return (
    <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const RepCard = ({ rep, isDark, onDeepDive }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = rep.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const pctColor = rep.pct >= 80 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : rep.pct >= 60 ? (isDark ? 'text-amber-400' : 'text-amber-600') : (isDark ? 'text-red-400' : 'text-red-500');
  const barColor = rep.pct >= 80 ? 'bg-emerald-500' : rep.pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className={`${cardBg} border ${rep.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border} rounded-xl p-3 relative group`} data-testid={`rep-card-${rep.name.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-[11px] font-semibold ${tp}`}>{rep.name}</p>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold ${pctColor}`}>{rep.pct}%</span>
          {onDeepDive && (
            <button
              onClick={onDeepDive}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
              data-testid={`rep-card-deepdive-${rep.name.replace(/\s+/g, '-').toLowerCase()}`}
              title="Deep Dive"
            >
              <Zap className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>
      <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'} mb-2`}>
        <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${rep.pct}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <div><p className={`text-[9px] ${ts}`}>Quota</p><p className={`text-[10px] font-bold ${tp}`}>${(rep.quota / 1000).toFixed(0)}K</p></div>
        <div><p className={`text-[9px] ${ts}`}>Closed</p><p className={`text-[10px] font-bold ${tp}`}>${(rep.attainment / 1000).toFixed(0)}K</p></div>
        <div><p className={`text-[9px] ${ts}`}>Pipeline</p><p className={`text-[10px] font-bold ${tp}`}>${(rep.pipeline / 1000).toFixed(0)}K</p></div>
        <div><p className={`text-[9px] ${ts}`}>Win Rate</p><p className={`text-[10px] font-bold ${tp}`}>{rep.winRate}</p></div>
      </div>
    </div>
  );
};

/* Monthly Chart: HTML bars + overlaid SVG trend line with explicit sizing */
const MonthlyChart = ({ data, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const maxVal = Math.max(...data.map(d => Math.max(d.closed, d.target))) * 1.1;

  const chartH = 140; // px
  const svgW = 700; // fixed design width — will visually stretch with container via width=100%
  const closedColor = isDark ? '#10b981' : '#059669';
  const amberColor = isDark ? '#f59e0b' : '#d97706';
  const targetFill = isDark ? 'rgba(148, 163, 184, 0.30)' : 'rgba(100, 116, 139, 0.22)';
  const targetSwatchBorder = isDark ? 'rgba(148, 163, 184, 0.45)' : 'rgba(100, 116, 139, 0.35)';
  const lineColor = isDark ? '#a5b4fc' : '#6366f1';
  const dotInner = isDark ? '#0d1b2a' : '#f0ece4';

  // trend line points in viewBox coordinates (svgW × chartH)
  const points = data.map((d, i) => {
    const cx = ((i + 0.5) / data.length) * svgW;
    const cy = (1 - d.closed / maxVal) * chartH;
    return [cx, cy];
  });
  const poly = points.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  return (
    <div>
      <div className="relative" style={{ height: `${chartH}px` }}>
        {/* gridlines */}
        <div className="absolute inset-0">
          {[0.25, 0.5, 0.75].map((p, i) => (
            <div key={i} className="absolute left-0 right-0 border-t" style={{ top: `${p * 100}%`, borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }} />
          ))}
        </div>

        {/* bar columns */}
        <div className="absolute inset-0 flex items-end">
          {data.map((d, i) => {
            const closedPct = (d.closed / maxVal) * 100;
            const targetPct = (d.target / maxVal) * 100;
            const hit = d.closed >= d.target;
            return (
              <div key={i} className="flex-1 flex items-end justify-center gap-1.5 h-full">
                <div className="w-2.5 rounded-t-md" style={{ height: `${closedPct}%`, backgroundColor: hit ? closedColor : amberColor, opacity: 0.95 }} />
                <div className="w-2.5 rounded-t-md" style={{ height: `${targetPct}%`, backgroundColor: targetFill }} />
              </div>
            );
          })}
        </div>

        {/* SVG trend line overlay */}
        <svg
          className="absolute inset-0 pointer-events-none"
          width="100%"
          height="100%"
          viewBox={`0 0 ${svgW} ${chartH}`}
          preserveAspectRatio="none"
        >
          <polyline
            points={poly}
            fill="none"
            stroke={lineColor}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {points.map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r="4" fill={lineColor} stroke={dotInner} strokeWidth="1.5" />
          ))}
        </svg>
      </div>

      {/* month / value labels */}
      <div className="flex mt-2">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center">
            <p className={`text-[10px] font-medium ${tp}`}>{d.month}</p>
            <p className={`text-[9px] ${ts}`}>${(d.closed / 1000).toFixed(0)}K</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-1.5">
        <span className={`text-[9px] flex items-center gap-1 ${ts}`}>
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: closedColor, opacity: 0.95 }} /> Closed
        </span>
        <span className={`text-[9px] flex items-center gap-1 ${ts}`}>
          <span className="inline-block w-2.5 h-2.5 rounded-sm border" style={{ backgroundColor: targetFill, borderColor: targetSwatchBorder }} /> Target
        </span>
        <span className={`text-[9px] flex items-center gap-1 ${ts}`}>
          <span className="inline-block w-3 h-[2px] rounded-sm" style={{ backgroundColor: lineColor }} /> Trend
        </span>
      </div>
    </div>
  );
};

const WinLossSummary = ({ data, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const total = data.won + data.lost;
  const wonPct = ((data.won / total) * 100).toFixed(0);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-4 rounded-full overflow-hidden flex" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
          <div className="h-full bg-emerald-500/50 rounded-l-full" style={{ width: `${wonPct}%` }} />
          <div className="h-full bg-red-500/30 rounded-r-full" style={{ width: `${100 - wonPct}%` }} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><p className={`text-[9px] ${ts}`}>Won</p><p className={`text-sm font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{data.won} deals</p><p className={`text-[9px] ${ts}`}>Avg: {data.avgWonSize}</p></div>
        <div><p className={`text-[9px] ${ts}`}>Lost</p><p className={`text-sm font-bold ${isDark ? 'text-red-400' : 'text-red-500'}`}>{data.lost} deals</p><p className={`text-[9px] ${ts}`}>Avg: {data.avgLostSize}</p></div>
      </div>
      <div className="mt-2">
        <p className={`text-[9px] ${ts}`}>Top win reason: <span className={`font-medium ${tp}`}>{data.topWinReason}</span></p>
        <p className={`text-[9px] mt-0.5 ${ts}`}>Top loss reason: <span className={`font-medium ${isDark ? 'text-red-400' : 'text-red-500'}`}>{data.topLossReason}</span></p>
      </div>
    </div>
  );
};

const InsightCard = ({ item, isDark, onDeepDive }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`} data-testid={`insight-card-${item.id}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <div className="flex items-center justify-between mt-1">
          <p className={`text-[9px] ${ts}`}>Sally · {item.time}</p>
          {onDeepDive && (
            <button
              onClick={onDeepDive}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
              data-testid={`insight-deepdive-${item.id}`}
            >
              <Zap className="w-2.5 h-2.5" /> Deep Dive
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const V4SalesReports = ({ isDark, onWorkflowToggle, workflowPanelOpen, activeUser, reportsAdHocScenario, onReportsAdHocDone }) => {
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [overviewDD, setOverviewDD] = useState(false);
  const [repDD, setRepDD] = useState(false);
  const [winlossDD, setWinlossDD] = useState(false);
  const [monthlyDD, setMonthlyDD] = useState(false);
  const [insightDDId, setInsightDDId] = useState(null);
  const [downloadState, setDownloadState] = useState('idle'); // idle | loading | done
  const messagesEndRef = useRef(null);

  const isDavid = activeUser?.id === 'sales';

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || REPORTS_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };

  const handleDownload = () => {
    if (downloadState !== 'idle') return;
    setDownloadState('loading');
    setTimeout(() => {
      setDownloadState('done');
      toast.success('Q1_Sales_Report_Feb.pdf downloaded', {
        description: 'Saved to Downloads · includes monthly trend, rep perf, win/loss, and Sally\'s insights.',
        duration: 3500,
      });
      setTimeout(() => setDownloadState('idle'), 1500);
    }, 1200);
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Sally about reports..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto' }} data-testid="reports-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.sally} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Sally</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="reports-send-btn">
              <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Overall Reports DD (David-only) ── */
  if (overviewDD && isDavid) {
    const s = DAVID_REPORTS_OVERALL_DD.overview;
    return <V4AdHocChat isDark={isDark} scenarioId={s.id} scenarios={{ [s.id]: s }} backLabel="Back to Reports" onBack={() => setOverviewDD(false)} />;
  }
  /* ── Rep Performance DD ── */
  if (repDD && isDavid) {
    const s = DAVID_REPORTS_REP_DD.reps;
    return <V4AdHocChat isDark={isDark} scenarioId={s.id} scenarios={{ [s.id]: s }} backLabel="Back to Reports" onBack={() => setRepDD(false)} />;
  }
  /* ── Win/Loss DD ── */
  if (winlossDD && isDavid) {
    const s = DAVID_REPORTS_WINLOSS_DD.winloss;
    return <V4AdHocChat isDark={isDark} scenarioId={s.id} scenarios={{ [s.id]: s }} backLabel="Back to Reports" onBack={() => setWinlossDD(false)} />;
  }
  /* ── Monthly Revenue DD ── */
  if (monthlyDD && isDavid) {
    const s = DAVID_REPORTS_MONTHLY_DD.monthly;
    return <V4AdHocChat isDark={isDark} scenarioId={s.id} scenarios={{ [s.id]: s }} backLabel="Back to Reports" onBack={() => setMonthlyDD(false)} />;
  }
  /* ── Per-Insight DD ── */
  if (insightDDId && isDavid) {
    const s = DAVID_REPORTS_INSIGHT_DD[insightDDId];
    if (s) {
      return <V4AdHocChat isDark={isDark} scenarioId={s.id} scenarios={{ [s.id]: s }} backLabel="Back to Reports" onBack={() => setInsightDDId(null)} />;
    }
  }
  /* ── Keypress A ── */
  if (reportsAdHocScenario && isDavid) {
    return <V4AdHocChat isDark={isDark} scenarioId={reportsAdHocScenario} scenarios={DAVID_REPORTS_KEYPRESS} backLabel="Back to Reports" onBack={() => onReportsAdHocDone?.()} skipFirstUserDelay />;
  }

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="reports-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`}><ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Reports</span></button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.sally} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  const d = SALES_REPORTS_DATA;
  return (
    <div className="flex flex-col h-full" data-testid="reports-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Reports</p>
          </div>
          {isDavid && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                disabled={downloadState !== 'idle'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${downloadState === 'done' ? (isDark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-500/10 text-emerald-600') : (isDark ? 'bg-white/[0.06] text-white hover:bg-white/[0.10]' : 'bg-black/[0.04] text-slate-800 hover:bg-black/[0.08]')}`}
                data-testid="reports-download-btn"
              >
                {downloadState === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : downloadState === 'done' ? <Check className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
                {downloadState === 'loading' ? 'Downloading…' : downloadState === 'done' ? 'Downloaded' : 'Download'}
              </button>
              <button
                onClick={() => setOverviewDD(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}
                data-testid="reports-deep-dive-btn"
              >
                <Search className="w-3.5 h-3.5" /> Deep Dive
              </button>
            </div>
          )}
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        {/* Activity Metrics */}
        <div className="grid grid-cols-6 gap-3">
          {d.activityMetrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Monthly trend + Win/Loss */}
        <div className="grid grid-cols-2 gap-4">
          <div className={`${cardBg} border ${border} rounded-xl p-4`} data-testid="monthly-revenue-card">
            <div className="flex items-center justify-between mb-3">
              <p className={`text-[11px] font-semibold ${tp}`}>Monthly Revenue Trend</p>
              {isDavid && (
                <button
                  onClick={() => setMonthlyDD(true)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
                  data-testid="monthly-deepdive-btn"
                >
                  <Zap className="w-2.5 h-2.5" /> Deep Dive
                </button>
              )}
            </div>
            <MonthlyChart data={d.monthlyTrend} isDark={isDark} />
          </div>
          <div className={`${cardBg} border ${border} rounded-xl p-4`} data-testid="winloss-card">
            <div className="flex items-center justify-between mb-3">
              <p className={`text-[11px] font-semibold ${tp}`}>Win / Loss Analysis</p>
              {isDavid && (
                <button
                  onClick={() => setWinlossDD(true)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
                  data-testid="winloss-deepdive-btn"
                >
                  <Zap className="w-2.5 h-2.5" /> Deep Dive
                </button>
              )}
            </div>
            <WinLossSummary data={d.winLoss} isDark={isDark} />
          </div>
        </div>

        {/* Rep Performance */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Rep Performance</p>
            </div>
            {isDavid && (
              <button
                onClick={() => setRepDD(true)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
                data-testid="rep-deepdive-btn"
              >
                <Zap className="w-2.5 h-2.5" /> Deep Dive
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {d.repPerformance.map(rep => <RepCard key={rep.name} rep={rep} isDark={isDark} onDeepDive={isDavid ? () => setRepDD(true) : null} />)}
          </div>
        </div>

        {/* Sally's Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.sally} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Sally's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {d.insights.map(item => (
              <InsightCard
                key={item.id}
                item={item}
                isDark={isDark}
                onDeepDive={isDavid && DAVID_REPORTS_INSIGHT_DD[item.id] ? () => setInsightDDId(item.id) : null}
              />
            ))}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4SalesReports;
